import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Coins, Gift, Users } from 'lucide-react'
import ProfileLink from '../../../components/ProfileLink'
import { personName, roleBadgeClass, roleLabel } from '../utils/teamFormat'
import { clampPct, formatSigned, modeIcon, modeLabel, profitClass } from '../utils/teamStats'
import { CARD_CLASS } from '../utils/teamUi'
import { formatPrice } from '../../../utils/formatters'
import { hapticImpact } from '../../../utils/haptic'

/**
 * Вклад участников в командные игры (docs/team-stats-api.md, §3, блок `members`).
 *
 * Список приходит отсортированным по `xp` — это и есть рейтинг вклада, свою
 * сортировку не наводим. `role: null` и `is_member: false` — игрок уже вышел
 * из банды, но за период играл: строка приглушена и помечена «вышел».
 * `given` / `received` бывают ненулевыми только в классике и кейсбатле.
 */
const TeamMemberStatsList = ({ members, accent }) => {
  const { t } = useTranslation()
  const [openSlug, setOpenSlug] = useState(null)

  const list = Array.isArray(members) ? members : []

  return (
    <div className={`${CARD_CLASS} p-4`}>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}1A` }}>
          <Users size={13} style={{ color: accent }} />
        </span>
        {t('team.stats.membersTitle', 'Вклад участников')}
      </h2>

      {list.length === 0 ? (
        <p className="text-sm text-text-secondary">
          {t('team.stats.membersEmpty', 'За период в командных играх никто не участвовал')}
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((member) => {
            const name = personName(member, t)
            const isOpen = openSlug === member.slug
            const xpPct = clampPct(member.xp_pct)
            const isFormer = member.is_member === false
            const TopIcon = member.top_mode ? modeIcon(member.top_mode) : null
            const modes = Array.isArray(member.modes) ? member.modes : []

            return (
              <div
                key={member.slug}
                className={`relative overflow-hidden rounded-xl border border-white/5 bg-dark-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${isFormer ? 'opacity-60' : ''}`}
              >
                {/* Доля в XP — фоновая полоса, строка от неё не растёт в высоту */}
                <div
                  className="pointer-events-none absolute inset-y-0 left-0"
                  style={{
                    width: `${xpPct}%`,
                    backgroundImage: `linear-gradient(90deg, ${accent}1F, transparent)`
                  }}
                />

                <button
                  onClick={() => {
                    hapticImpact('light')
                    setOpenSlug(isOpen ? null : member.slug)
                  }}
                  className="relative block w-full p-3 text-left transition-all active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3">
                    <ProfileLink slug={member.slug} className="shrink-0">
                      <span className="block h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-surface">
                        {member.ava ? (
                          <img src={member.ava} alt={name} className="h-full w-full object-cover" />
                        ) : null}
                      </span>
                    </ProfileLink>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <ProfileLink slug={member.slug} className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-white">{name}</span>
                        </ProfileLink>
                        {isFormer ? (
                          <span className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                            {t('team.stats.former', 'вышел')}
                          </span>
                        ) : member.role ? (
                          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${roleBadgeClass(member.role)}`}>
                            {roleLabel(member.role, t)}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-text-secondary">
                        <span className="tabular-nums">
                          {t('team.stats.memberGames', '{{games}} игр · {{rate}}% побед', {
                            games: member.games ?? 0,
                            rate: member.win_rate ?? 0
                          })}
                        </span>
                        <span className={`tabular-nums ${profitClass(member.profit)}`}>
                          {formatSigned(member.profit ?? 0)}
                        </span>
                        {TopIcon && (
                          <span className="flex items-center gap-1">
                            <TopIcon size={11} style={{ color: accent }} />
                            {modeLabel(member.top_mode, t)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className="text-sm font-bold tabular-nums" style={{ color: accent }}>{xpPct}%</p>
                      <p className="text-[10px] tabular-nums text-text-secondary">
                        {t('team.stats.memberXp', '{{xp}} XP', { xp: formatPrice(member.xp ?? 0) })}
                      </p>
                    </div>

                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>

                {isOpen && (
                  <div className="relative border-t border-white/5 p-3">
                    <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
                      <div>
                        <p className="text-text-secondary">{t('team.stats.bet', 'Поставлено')}</p>
                        <p className="font-semibold tabular-nums text-white">{formatPrice(member.bet ?? 0)}</p>
                      </div>
                      <div>
                        <p className="text-text-secondary">{t('team.stats.win', 'Получено')}</p>
                        <p className="font-semibold tabular-nums text-white">{formatPrice(member.win ?? 0)}</p>
                      </div>
                      {/* Классик и кейсбатл: возврат ставок и делёжка банка внутри банды */}
                      <div>
                        <p className="flex items-center gap-1 text-text-secondary">
                          <Gift size={10} />
                          {t('team.stats.memberGiven', 'Отдал своим')}
                        </p>
                        <p className="font-semibold tabular-nums text-white">{formatPrice(member.given ?? 0)}</p>
                      </div>
                      <div>
                        <p className="flex items-center gap-1 text-text-secondary">
                          <Coins size={10} />
                          {t('team.stats.memberReceived', 'Получил от своих')}
                        </p>
                        <p className="font-semibold tabular-nums text-white">{formatPrice(member.received ?? 0)}</p>
                      </div>
                    </div>

                    {modes.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {modes.map((mode) => {
                          const Icon = modeIcon(mode.kind)
                          return (
                            <div key={mode.kind} className="flex items-center justify-between gap-3 text-[11px]">
                              <span className="flex min-w-0 items-center gap-1.5 text-white">
                                <Icon size={12} style={{ color: accent }} className="shrink-0" />
                                <span className="truncate">{modeLabel(mode.kind, t)}</span>
                              </span>
                              <span className="flex shrink-0 items-center gap-2 tabular-nums text-text-secondary">
                                <span>{t('team.stats.modeGamesShort', '{{count}} игр', { count: mode.games ?? 0 })}</span>
                                <span>{formatPrice(mode.bet ?? 0)}</span>
                                <span className={profitClass(mode.profit)}>{formatSigned(mode.profit ?? 0)}</span>
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TeamMemberStatsList
